import type { pendingInviteDTOSchema } from '@tempo-io/id-dto';
import { Badge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import { Card } from '@tempo-io/sui-card';
import { CheckCircle, CorporateFare, Email, Person, Schedule } from '@tempo-io/sui-icons';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Invite = z.output<typeof pendingInviteDTOSchema>;

type InvitesListProps = {
  invites: Invite[];
  onAcceptInvite: (invite: Invite) => void | Promise<void>;
  onSkip?: () => void;
  skipButtonText?: string;
  isAcceptingInvite?: boolean;
};

type InviteCardProps = {
  invite: Invite;
  isAccepting: boolean;
  disabled: boolean;
  onAccept: (invite: Invite) => void;
};

function InviteCard({ invite, isAccepting, disabled, onAccept }: InviteCardProps) {
  const { t } = useTranslation();
  const inviterDisplayName = invite.inviter.name ?? invite.inviter.email;

  return (
    <Card className="gap-0 overflow-hidden rounded-lg border-border-neutral-subtle py-0 shadow-xs">
      <div className="flex items-center justify-between gap-4 bg-bg-surface-raised p-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex shrink-0 items-center rounded-md bg-bg-neutral-bold p-2">
            <CorporateFare className="size-5 text-icon-neutral-inverted" />
          </div>
          <h3 className="truncate text-base font-semibold leading-5 tracking-tight text-text-neutral-primary">
            {invite.organization.displayName}
          </h3>
        </div>

        <Button className="shrink-0" onClick={() => onAccept(invite)} disabled={disabled}>
          {isAccepting
            ? t(($) => $.invites.accept.accepting)
            : t(($) => $.invites.accept.acceptInvite)}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border-neutral-subtle bg-bg-surface-base px-5 py-3">
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Person className="size-5 text-icon-neutral-secondary" />
            <span className="text-sm font-medium text-text-neutral-secondary">
              {t(($) => $.invites.accept.roleLabel)}
            </span>
          </div>
          <Badge className="px-2.5 capitalize">{invite.role}</Badge>
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <div className="flex shrink-0 items-center gap-1.5">
            <Email className="size-5 text-icon-neutral-secondary" />
            <span className="text-sm font-medium text-text-neutral-secondary">
              {t(($) => $.invites.accept.invitedByLabel)}
            </span>
          </div>
          <span className="truncate text-sm font-medium text-text-neutral-primary">
            {inviterDisplayName}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Schedule className="size-5 text-icon-neutral-secondary" />
          <span className="text-sm font-medium text-text-neutral-secondary">
            {formatDistanceToNow(new Date(invite.createdAt), {
              addSuffix: true,
            })}
          </span>
        </div>
      </div>
    </Card>
  );
}

export function InvitesList({
  invites,
  onAcceptInvite,
  onSkip,
  skipButtonText,
  isAcceptingInvite,
}: InvitesListProps) {
  const { t } = useTranslation();
  const [acceptingId, setAcceptingId] = useState<number | null>(null);
  const isEmpty = invites.length === 0;
  const handleAccept = useCallback(
    async (invite: Invite) => {
      setAcceptingId(invite.id);
      try {
        await onAcceptInvite(invite);
      } finally {
        setAcceptingId(null);
      }
    },
    [onAcceptInvite],
  );

  return (
    <div className="space-y-4">
      {invites.map((invite) => (
        <InviteCard
          key={invite.id}
          invite={invite}
          isAccepting={acceptingId === invite.id}
          disabled={isAcceptingInvite || acceptingId === invite.id}
          onAccept={handleAccept}
        />
      ))}

      {isEmpty && (
        <div className="flex h-full w-full items-center justify-center p-6">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-bg-success-subtler dark:bg-bg-success-bold mb-4">
                <CheckCircle className="h-8 w-8 text-text-success dark:text-text-success/70" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold tracking-tight">
                  {t(($) => $.invites.accept.allAccepted)}
                </h2>
                <p className="text-text-neutral-secondary">
                  {t(($) => $.invites.accept.allAcceptedDescription)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {onSkip && !isEmpty && (
        <div className="flex justify-center pt-4">
          <Button variant="ghost" onClick={onSkip}>
            {skipButtonText ?? t(($) => $.common.skipForNow)}
          </Button>
        </div>
      )}
    </div>
  );
}
